<header class="so__head">
  <div class="so__intro">
    <h2 class="bk-eyebrow" id="so-title">Messages sent</h2>
    <p class="so__period">{{ periodText }}</p>
  </div>

  <div class="so__controls">
    <label class="bk-sr-only" for="so-period">Period</label>
    <bk-select
      class="so__preset"
      inputId="so-period"
      noun="period"
      [options]="presetOptions"
      [value]="preset"
      (valueChange)="choosePreset($event)"
    />
    <div class="so__seg" role="radiogroup" aria-label="Group by">
      @for (option of intervalOptions; track option.value) {
        <button
          type="button"
          role="radio"
          class="so__seg-btn"
          [attr.aria-checked]="interval === option.value"
          [disabled]="!fits(option.value)"
          (click)="chooseInterval(option.value)"
        >
          {{ option.label }}
        </button>
      }
    </div>
  </div>
</header>

@if (preset === "custom") {
  <div class="so__custom" role="group" aria-label="Custom period">
    <div class="so__when">
      <span class="so__when-label" id="so-from">From</span>
      <input
        type="date"
        class="bk-field__input so__date"
        aria-labelledby="so-from"
        [value]="customFromDate"
        (change)="onCustomChange('fromDate', $event)"
      />
      <input
        type="time"
        class="bk-field__input so__time"
        aria-label="From time"
        [value]="customFromTime"
        (change)="onCustomChange('fromTime', $event)"
      />
    </div>
    <div class="so__when">
      <span class="so__when-label" id="so-to">To</span>
      <input
        type="date"
        class="bk-field__input so__date"
        aria-labelledby="so-to"
        [value]="customToDate"
        (change)="onCustomChange('toDate', $event)"
      />
      <input
        type="time"
        class="bk-field__input so__time"
        aria-label="To time"
        [value]="customToTime"
        (change)="onCustomChange('toTime', $event)"
      />
    </div>
    @if (rangeError) {
      <p class="so__range-error" role="alert">
        <span class="bk-sym" aria-hidden="true">error</span>
        {{ rangeError }}
      </p>
    }
  </div>
}

@if (problem === "unavailable") {
  <p class="so__note">
    <span class="bk-sym" aria-hidden="true">schedule</span>
    Sent-message counts will show here once the backend update is deployed.
  </p>
} @else if (problem === "failed" && !data) {
  <p class="so__note">
    <span class="bk-sym" aria-hidden="true">error</span>
    The counts didn't load.
    <button type="button" class="so__retry" (click)="load()">Try again</button>
  </p>
} @else {
  <!-- The period in four figures -->
  <dl class="so__figures" [class.is-stale]="loading && data">
    <div class="so__figure so__figure--lead">
      <dt>Sent</dt>
      <dd>
        @if (data) {
          {{ formatCount(data.total) }}
        } @else {
          <span class="bk-skel so__skel so__skel--lead" aria-hidden="true"></span>
        }
      </dd>
    </div>
    <div class="so__figure">
      <dt><span class="so__dot so__dot--ok" aria-hidden="true"></span>Delivered</dt>
      <dd>
        @if (data) {
          {{ formatCount(data.delivered) }}
          @if (share) {
            <span class="so__share">{{ share }}</span>
          }
        } @else {
          <span class="bk-skel so__skel" aria-hidden="true"></span>
        }
      </dd>
    </div>
    <div class="so__figure">
      <dt><span class="so__dot so__dot--bad" aria-hidden="true"></span>Failed</dt>
      <dd>
        @if (data) {
          {{ formatCount(data.failed) }}
        } @else {
          <span class="bk-skel so__skel" aria-hidden="true"></span>
        }
      </dd>
    </div>
    <div class="so__figure">
      <dt><span class="so__dot so__dot--wait" aria-hidden="true"></span>No receipt yet</dt>
      <dd>
        @if (data) {
          {{ formatCount(data.pending) }}
        } @else {
          <span class="bk-skel so__skel" aria-hidden="true"></span>
        }
      </dd>
    </div>
  </dl>

  <!-- What the highlighted bar holds; screen readers hear it as it changes -->
  <p class="so__readout" aria-live="polite">
    @if (readout) {
      {{ readout }}
    } @else if (data && data.total) {
      <span class="so__hint so__hint--point">Point at a bar to see its numbers.</span>
      <span class="so__hint so__hint--tap">Tap a bar to see its numbers.</span>
    }
  </p>

  <div
    class="so__chart"
    tabindex="0"
    role="img"
    [attr.aria-label]="chartSummary"
    [class.is-stale]="loading && data"
    (keydown)="onChartKey($event)"
    (blur)="active = null"
  >
    @if (data) {
      <span class="so__max" aria-hidden="true">{{ formatCount(max) }}</span>
      <div class="so__bars" [class.has-active]="active !== null" (pointerleave)="active = null">
        @for (bucket of buckets; track bucket.start.getTime(); let i = $index) {
          <div class="so__slot" (pointerenter)="active = i">
            <div class="so__bar" [class.is-active]="active === i" [class.is-empty]="!bucket.total" [style.height]="height(bucket)">
              <span class="so__part so__part--delivered" [style.flex-grow]="bucket.delivered"></span>
              <span class="so__part so__part--pending" [style.flex-grow]="pendingOf(bucket)"></span>
              <span class="so__part so__part--failed" [style.flex-grow]="bucket.failed"></span>
            </div>
          </div>
        }
      </div>
      @if (!data.total) {
        <p class="so__empty">Nothing was sent in this period.</p>
      }
      <div class="so__axis" aria-hidden="true">
        @for (tick of ticks; track tick.left) {
          <span class="so__tick" [style.left.%]="tick.left">{{ tick.label }}</span>
        }
      </div>
    } @else {
      <div class="so__bars so__bars--skel" aria-hidden="true">
        @for (row of [38, 62, 45, 80, 55, 70, 40, 66, 52, 74, 48, 58]; track $index) {
          <div class="so__slot"><span class="bk-skel so__skel-bar" [style.height.%]="row"></span></div>
        }
      </div>
    }
  </div>
}
